import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { dashboardAPI, fixedOverheadAPI, variableOverheadAPI } from '../../utils/api';
import { calculateRecipeCostPerServing, getTopUsedMaterials, calculateUnitCostWithVat, formatPercentage, formatNumberForDisplay } from '../../utils/calculations';
import { useVat } from '../../contexts/VatContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useTheme } from '../../contexts/ThemeContext';
import Modal from '../Layout/Modal.jsx';
import DataGrid from '../Layout/DataGrid.jsx';

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const { vatPercentage } = useVat();
  const { formatCurrency, getCurrentCurrency } = useCurrency();
  const { theme, isDark } = useTheme();
  const [recipes, setRecipes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [totalMaterialsCount, setTotalMaterialsCount] = useState(0);
  const [totalRecipesCount, setTotalRecipesCount] = useState(0);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [breakEvenData, setBreakEvenData] = useState(null);
  const [isLoadingBreakEven, setIsLoadingBreakEven] = useState(false);
  const [totalMonthlyFixedCosts, setTotalMonthlyFixedCosts] = useState(0);
  const [totalMonthlyVariableCosts, setTotalMonthlyVariableCosts] = useState(0);
  const [materialUsageStats, setMaterialUsageStats] = useState({
    usedInDishes: 0,
    unusedMaterials: 0
  });

  useEffect(() => {
    loadData();
    loadBreakEvenData();
    loadFixedCostsData();
    loadVariableCostsData();
  }, []);
  
  // Refresh dashboard data when VAT changes
  useEffect(() => {
    loadData();
    loadBreakEvenData();
    loadFixedCostsData();
    loadVariableCostsData();
  }, [vatPercentage]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const stats = await dashboardAPI.getStats();
      
      // Handle the correct data structure from backend
      setRecipes(stats.mostExpensiveRecipes || []);
      setMaterials(stats.topMaterialsByCost || []);
      setTotalMaterialsCount(stats.counts?.materials || 0);
      setTotalRecipesCount(stats.counts?.recipes || 0);
      setMaterialUsageStats({
        usedInDishes: stats.materialUsageStats?.usedInDishes || 0,
        unusedMaterials: stats.materialUsageStats?.unusedMaterials || 0
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data. Please try again.');
      setRecipes([]);
      setMaterials([]);
      setTotalMaterialsCount(0);
      setTotalRecipesCount(0);
      setMaterialUsageStats({ usedInDishes: 0, unusedMaterials: 0 });
    } finally {
      setIsLoading(false);
    }
  };

  const loadBreakEvenData = async () => {
    try {
      setIsLoadingBreakEven(true);
      const data = await dashboardAPI.getBreakEven();
      setBreakEvenData(data);
    } catch (error) {
      console.error('Error loading break-even data:', error);
      setBreakEvenData(null);
    } finally {
      setIsLoadingBreakEven(false);
    }
  };

  const loadFixedCostsData = async () => {
    try {
      const data = await fixedOverheadAPI.getDistributionSummary();
      setTotalMonthlyFixedCosts(data.total_monthly_fixed_costs || 0);
    } catch (error) {
      console.error('Error loading fixed costs data:', error);
      setTotalMonthlyFixedCosts(0);
    }
  };

  const loadVariableCostsData = async () => {
    try {
      const data = await variableOverheadAPI.getDistributionSummary();
      setTotalMonthlyVariableCosts(data.total_monthly_variable_costs || 0);
    } catch (error) {
      console.error('Error loading variable costs data:', error);
      setTotalMonthlyVariableCosts(0);
    }
  };

  // Prepare data for charts with proper field mapping
  const dishCostData = recipes.map(recipe => ({
    name: i18n.language === 'ar' ? (recipe.name_ar || recipe.name_en || 'Unknown') : (recipe.name_en || 'Unknown'),
    cost: recipe.cost_per_serving || 0,
    servings: recipe.servings || 1
  })).sort((a, b) => b.cost - a.cost);

  const topMaterialsData = materials.map((material, index) => {
    return {
      id: material.id || `material-${index}`, // Add unique ID
      name: i18n.language === 'ar' ? (material.name_ar || material.name_en || 'Unknown') : (material.name_en || 'Unknown'),
      cost: material.total_cost_in_recipes || 0,
      unitCost: calculateUnitCostWithVat(material, vatPercentage), // Calculate unit cost dynamically with VAT awareness
      quantity: material.usage_count || 0, // This is actually usage count, not quantity
      usageCount: material.usage_count || 0
    };
  });
  
  // For chart - show only materials with usage count > 0 and cost > 0
  const topMaterialsForChart = topMaterialsData
    .filter(material => material.usageCount > 0 && material.cost > 0) // Only show materials with usage and cost
    .sort((a, b) => b.cost - a.cost); // Sort by total cost in recipes

  // Use real data only
  const chartData = topMaterialsForChart;

  // Calculate total cost for percentage calculations
  const totalCost = chartData.reduce((sum, item) => sum + item.cost, 0);
  
  // Add percentage to chart data
  const chartDataWithPercentages = chartData.map(item => ({
    ...item,
    percentage: totalCost > 0 ? (item.cost / totalCost) * 100 : 0
  }));

  // Define columns for DataGrid tables
  const dishColumns = [
    {
      key: 'name',
      label: t('common.name'),
      sortable: true
    },
    {
      key: 'servings',
      label: t('recipes.numberOfServings'),
      type: 'number',
      sortable: true
    },
    {
      key: 'cost',
      label: t('recipes.costPerServing'),
      type: 'currency',
      sortable: true,
      render: (value) => formatCurrency(value)
    },
    {
      key: 'totalCost',
      label: 'Total Cost',
      type: 'currency',
      sortable: true,
      render: (value, row) => formatCurrency(row.cost * row.servings)
    }
  ];

  const materialColumns = [
    {
      key: 'name',
      label: t('common.name'),
      sortable: true
    },
    {
      key: 'usageCount',
      label: 'Usage Count',
      type: 'number',
      sortable: true
    },
    {
      key: 'unitCost',
      label: 'Unit Cost',
      type: 'currency',
      sortable: true,
      render: (value) => formatCurrency(value)
    },
    {
      key: 'cost',
      label: 'Total Cost in Recipes',
      type: 'currency',
      sortable: true,
      render: (value) => formatCurrency(value)
    },
    {
      key: 'status',
      label: 'Status',
      type: 'badge',
      sortable: false,
      render: (value, row) => {
        if (row.usageCount > 0) {
          return 'Used in dishes';
        } else if (row.unitCost > 0) {
          return 'Available (not used)';
        } else {
          return 'No unit cost set';
        }
      }
    }
  ];

  // Prepare data for DataGrid
  const dishGridData = dishCostData.map((dish, index) => ({
    id: `dish-${index}`,
    ...dish,
    totalCost: dish.cost * dish.servings
  }));

  const materialGridData = topMaterialsData.map((material, index) => ({
    id: material.id || `material-${index}`,
    ...material,
    status: material.usageCount > 0 ? 'Used in dishes' : 
            material.unitCost > 0 ? 'Available (not used)' : 'No unit cost set'
  }));

  // Colors for charts - using theme colors
  const COLORS = isDark 
    ? ['#7162', '#6382', '#7482', '#7124', '#7546', '#82CA9D', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4']
    : ['#5417', '#7042', '#5679', '#6356', '#4509', '#82CA9D', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-card-foreground">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-card-foreground">
              {entry.name}: {formatNumberForDisplay(formatCurrency(entry.value))} (Usage Cost)
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom Y-axis formatter for Arabic numerals
  const CustomYAxisTick = ({ x, y, payload }) => {
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={4} textAnchor="end" fill="currentColor" className="text-xs">
          {formatNumberForDisplay(payload.value)}
        </text>
      </g>
    );
  };


  return (
    <div className={`space-y-6 bg-background text-foreground min-h-screen p-6 ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
      {/* Header */}
      <div 
        className="header-container"
        style={{
          textAlign: i18n.language === 'ar' ? 'right' : 'left',
          direction: i18n.language === 'ar' ? 'rtl' : 'ltr'
        }}
      >
        <h1 
          className="text-2xl font-bold text-foreground"
          style={{
            textAlign: i18n.language === 'ar' ? 'right' : 'left'
          }}
        >
          {t('dashboard.title')}
        </h1>
        <p 
          className="mt-2 text-muted-foreground"
          style={{
            textAlign: i18n.language === 'ar' ? 'right' : 'left'
          }}
        >
          {t('dashboard.costAnalysisAndInsights')}
        </p>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className={`flex items-center justify-center py-12 ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className={`text-muted-foreground ${i18n.language === 'ar' ? 'mr-2' : 'ml-2'}`}>Loading dashboard data...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
          <div className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-destructive" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className={i18n.language === 'ar' ? 'mr-3' : 'ml-3'}>
              <h3 className={`text-sm font-medium text-destructive ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>Error</h3>
              <div className={`mt-2 text-sm text-destructive/80 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                <p>{error}</p>
              </div>
              <div className={`mt-4 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                <button
                  onClick={loadData}
                  className="text-sm font-medium text-destructive hover:text-destructive/80 underline"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Content - Only show when not loading and no error */}
      {!isLoading && !error && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-card border border-border p-6 rounded-lg shadow">
              <div className={`flex items-center ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
                <div className="p-2 bg-chart-2/10 rounded-lg">
                  <svg className="w-6 h-6 text-chart-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <div className={i18n.language === 'ar' ? 'mr-4' : 'ml-4'}>
                  <p className={`text-sm font-medium text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.totalMaterials')}</p>
                  <p className={`text-2xl font-bold text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{formatNumberForDisplay(totalMaterialsCount)}</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border p-6 rounded-lg shadow">
              <div className={`flex items-center ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
                <div className="p-2 bg-primary/10 rounded-lg">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className={i18n.language === 'ar' ? 'mr-4' : 'ml-4'}>
                  <p className={`text-sm font-medium text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.totalDishes')}</p>
                  <p className={`text-2xl font-bold text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{formatNumberForDisplay(totalRecipesCount)}</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border p-6 rounded-lg shadow">
              <div className={`flex items-center ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
                <div className="p-2 bg-chart-4/10 rounded-lg">
                  <svg className="w-6 h-6 text-chart-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className={i18n.language === 'ar' ? 'mr-4' : 'ml-4'}>
                  <p className={`text-sm font-medium text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.totalMonthlyFixedCosts')}</p>
                  <p className={`text-2xl font-bold text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                    {formatNumberForDisplay(formatCurrency(totalMonthlyFixedCosts))}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border p-6 rounded-lg shadow">
              <div className={`flex items-center ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
                <div className="p-2 bg-chart-3/10 rounded-lg">
                  <svg className="w-6 h-6 text-chart-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div className={i18n.language === 'ar' ? 'mr-4' : 'ml-4'}>
                  <p className={`text-sm font-medium text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.totalMonthlyVariableCosts')}</p>
                  <p className={`text-2xl font-bold text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                    {formatNumberForDisplay(formatCurrency(totalMonthlyVariableCosts))}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Break-Even Analysis Section */}
          {breakEvenData && !isLoadingBreakEven && (
            <div className={`space-y-1 text-sm text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
              <p>{t('dashboard.breakEvenEqualDistributionNote')}</p>
              {breakEvenData.excludedExpectedOrders > 0 && (
                <p>
                  {t('dashboard.breakEvenExcludedOrdersNote', {
                    count: formatNumberForDisplay(breakEvenData.excludedExpectedOrders.toLocaleString()),
                  })}
                </p>
              )}
              {breakEvenData.excludedFixedCosts > 0 && (
                <p>
                  {t('dashboard.breakEvenExcludedFixedCostsNote', {
                    amount: formatNumberForDisplay(formatCurrency(breakEvenData.excludedFixedCosts)),
                  })}
                </p>
              )}
              {breakEvenData.partialPricingWarnings?.length > 0 && (
                <ul className={`list-disc space-y-1 ${i18n.language === 'ar' ? 'pr-5' : 'pl-5'}`}>
                  {breakEvenData.partialPricingWarnings.map((warning) => (
                    <li key={warning.categoryId}>
                      {t('dashboard.breakEvenPartialPricingWarning', {
                        category: warning.categoryName,
                        priced: warning.pricedDishes,
                        total: warning.totalDishes,
                      })}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Break-Even Units */}
            <div className="bg-card border border-border p-6 rounded-lg shadow">
              <div className={`flex items-center mb-4 ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
                <div className="p-2 bg-destructive/10 rounded-lg shrink-0">
                  <svg className="w-6 h-6 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className={`min-w-0 ${i18n.language === 'ar' ? 'mr-3' : 'ml-3'}`}>
                  <h3 className="text-lg font-medium text-card-foreground text-left">
                    {t('dashboard.breakEvenUnits')}
                  </h3>
                  <p className="text-sm text-muted-foreground text-left">
                    {t('dashboard.breakEvenUnitsDescription')}
                  </p>
                </div>
              </div>
              
              {isLoadingBreakEven ? (
                <div className="flex items-center justify-center h-24">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-destructive"></div>
                </div>
              ) : breakEvenData ? (
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-destructive mb-2">
                      {breakEvenData.breakEvenUnits ? breakEvenData.breakEvenUnits.toLocaleString() : 'N/A'}
                    </div>
                    <div className="text-sm text-muted-foreground">{t('dashboard.units')}</div>
                  </div>
                  
                  <div className="border-t border-border pt-4 space-y-2 text-sm">
                    <div className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse justify-between' : 'justify-between'}`}>
                      <span className={`text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.totalFixedCosts')}</span>
                      <span className={`font-medium text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{formatNumberForDisplay(formatCurrency(breakEvenData.totalFixedCosts || 0))}</span>
                    </div>
                    <div className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse justify-between' : 'justify-between'}`}>
                      <span className={`text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.averageContributionMargin')}</span>
                      <span className={`font-medium text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{formatNumberForDisplay(formatCurrency(breakEvenData.averageContributionMargin || 0))}</span>
                    </div>
                    <div className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse justify-between' : 'justify-between'}`}>
                      <span className={`text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.totalExpectedOrders')}</span>
                      <span className={`font-medium text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{breakEvenData.totalExpectedOrders ? formatNumberForDisplay(breakEvenData.totalExpectedOrders.toLocaleString()) : '0'}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  <p>No pricing data available for break-even analysis</p>
                  <p className="text-sm mt-1">Add pricing information to your dishes to see break-even calculations</p>
                </div>
              )}
            </div>

            {/* Break-Even Sales */}
            <div className="bg-card border border-border p-6 rounded-lg shadow">
              <div className={`flex items-center mb-4 ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
                <div className="p-2 bg-chart-4/10 rounded-lg">
                  <svg className="w-6 h-6 text-chart-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <div className={`min-w-0 ${i18n.language === 'ar' ? 'mr-3' : 'ml-3'}`}>
                  <h3 className="text-lg font-medium text-card-foreground text-left">{t('dashboard.breakEvenSales')}</h3>
                  <p className="text-sm text-muted-foreground text-left">{t('dashboard.breakEvenSalesDescription')}</p>
                </div>
              </div>
              
              {isLoadingBreakEven ? (
                <div className="flex items-center justify-center h-24">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-chart-4"></div>
                </div>
              ) : breakEvenData ? (
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-chart-4 mb-2">
                      {breakEvenData.breakEvenSales ? formatNumberForDisplay(formatCurrency(breakEvenData.breakEvenSales)) : 'N/A'}
                    </div>
                    <div className="text-sm text-muted-foreground">{getCurrentCurrency().code}</div>
                  </div>
                  
                  <div className="border-t border-border pt-4 space-y-2 text-sm">
                    <div className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse justify-between' : 'justify-between'}`}>
                      <span className={`text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.weightedAverageSellingPrice')}</span>
                      <span className={`font-medium text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{formatNumberForDisplay(formatCurrency(breakEvenData.weightedAverageSellingPrice || 0))}</span>
                    </div>
                    <div className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse justify-between' : 'justify-between'}`}>
                      <span className={`text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.totalContribution')}</span>
                      <span className={`font-medium text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{formatNumberForDisplay(formatCurrency(breakEvenData.totalContribution || 0))}</span>
                    </div>
                    <div className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse justify-between' : 'justify-between'}`}>
                      <span className={`text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.breakEvenUnits')}</span>
                      <span className={`font-medium text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{breakEvenData.breakEvenUnits ? `${formatNumberForDisplay(breakEvenData.breakEvenUnits.toLocaleString())} ${t('dashboard.units')}` : 'N/A'}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  <p>No pricing data available for break-even analysis</p>
                  <p className="text-sm mt-1">Add pricing information to your dishes to see break-even calculations</p>
                </div>
              )}
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Used Materials */}
            <div className="bg-card border border-border p-6 rounded-lg shadow">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-card-foreground">
                  {t('dashboard.topMaterials')}
                </h3>
              </div>
              
              
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis tick={<CustomYAxisTick />} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="cost" fill="var(--chart-1)" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  <div className="text-center">
                    <p>{t('dashboard.noMaterialsUsedInDishes')}</p>
                    <p className="text-sm mt-1">{t('dashboard.addMaterialsToSeeBreakdown')}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Dish Cost Comparison */}
            <div className="bg-card border border-border p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-card-foreground mb-4">
                {t('dashboard.dishComparison')}
              </h3>
              {dishCostData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dishCostData.slice(0, 10)}>
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis tick={<CustomYAxisTick />} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="cost" fill="var(--chart-1)" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  <div className="text-center">
                    <p>{t('dashboard.noDishesForComparison')}</p>
                    <p className="text-sm mt-1">{t('dashboard.addDishesToSeeComparison')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>



      {/* Material Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Material Usage Statistics */}
        <div className="bg-card border border-border p-6 rounded-lg shadow">
          <h3 className={`text-lg font-medium text-card-foreground mb-4 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.materialUsageStats')}</h3>
          <div className="space-y-4">
            <div className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse justify-between' : 'justify-between'} items-center`}>
              <span className={`text-sm text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.totalMaterials')}</span>
              <span className={`font-semibold text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{formatNumberForDisplay(totalMaterialsCount)}</span>
            </div>
            <div className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse justify-between' : 'justify-between'} items-center`}>
              <span className={`text-sm text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.usedInDishes')}</span>
              <span className={`font-semibold text-chart-4 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                {formatNumberForDisplay(materialUsageStats.usedInDishes)}
              </span>
            </div>
            <div className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse justify-between' : 'justify-between'} items-center`}>
              <span className={`text-sm text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.unusedMaterials')}</span>
              <span className={`font-semibold text-chart-2 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                {formatNumberForDisplay(materialUsageStats.unusedMaterials)}
              </span>
            </div>
          </div>
        </div>

        {/* Top Cost Contributors */}
        <div className="bg-card border border-border p-6 rounded-lg shadow">
          <h3 className={`text-lg font-medium text-card-foreground mb-4 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.topCostContributors')}</h3>
          <div className="space-y-3">
            {topMaterialsData
              .filter(m => m.cost > 0)
              .slice(0, 5)
              .map((material, index) => (
                <div key={material.id} className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse justify-between' : 'justify-between'} items-center`}>
                  <div className={`flex items-center ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
                    <div 
                      className={`w-3 h-3 rounded-full ${i18n.language === 'ar' ? 'ml-2' : 'mr-2'}`}
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    ></div>
                    <span className={`text-sm font-medium truncate max-w-24 text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                      {material.name}
                    </span>
                  </div>
                  <span className={`text-sm font-semibold text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                    {formatNumberForDisplay(formatCurrency(material.cost))}
                  </span>
                </div>
              ))}
            {topMaterialsData.filter(m => m.cost > 0).length === 0 && (
              <p className="text-sm text-muted-foreground text-center">{t('dashboard.noMaterialsWithCostsFound')}</p>
            )}
          </div>
        </div>

        {/* Usage Distribution */}
        <div className="bg-card border border-border p-6 rounded-lg shadow">
          <h3 className={`text-lg font-medium text-card-foreground mb-4 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.usageDistribution')}</h3>
          <div className="space-y-3">
            <div className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse justify-between' : 'justify-between'} items-center`}>
              <span className={`text-sm text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.highUsageDishes')}</span>
              <span className={`font-semibold text-chart-1 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                {formatNumberForDisplay(topMaterialsData.filter(m => m.usageCount >= 3).length)}
              </span>
            </div>
            <div className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse justify-between' : 'justify-between'} items-center`}>
              <span className={`text-sm text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.mediumUsageDishes')}</span>
              <span className={`font-semibold text-chart-4 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                {formatNumberForDisplay(topMaterialsData.filter(m => m.usageCount >= 1 && m.usageCount < 3).length)}
              </span>
            </div>
            <div className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse justify-between' : 'justify-between'} items-center`}>
              <span className={`text-sm text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.unusedMaterials')}</span>
              <span className={`font-semibold text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                {formatNumberForDisplay(materialUsageStats.unusedMaterials)}
              </span>
            </div>
            <div className="mt-4 pt-3 border-t border-border">
              <div className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse justify-between' : 'justify-between'} items-center`}>
                <span className={`text-sm text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dashboard.totalCostInDishes')}</span>
                <span className={`font-semibold text-lg text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                  {formatNumberForDisplay(formatCurrency(
                    topMaterialsData.reduce((sum, m) => sum + (m.cost || 0), 0)
                  ), i18n.language)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )}

  {/* Recipe Details Modal */}
  <Modal isOpen={selectedRecipe !== null} onClose={() => setSelectedRecipe(null)} className="max-w-4xl max-h-screen overflow-y-auto">
    <div className={`flex justify-between items-center mb-4 ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
      <h2 className={`text-lg font-semibold text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
        {i18n.language === 'ar' ? (selectedRecipe?.name_ar || selectedRecipe?.name_en) : selectedRecipe?.name_en} - Details
      </h2>
      <button
        onClick={() => setSelectedRecipe(null)}
        className="text-muted-foreground hover:text-card-foreground"
      >
        <X className="h-6 w-6" />
      </button>
    </div>
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={i18n.language === 'ar' ? 'text-right' : 'text-left'}>
          <h3 className="font-medium text-card-foreground">Recipe Information</h3>
          <div className="mt-2 space-y-1 text-sm">
            <p><span className="font-medium text-card-foreground">English Name:</span> <span className="text-muted-foreground">{selectedRecipe?.name_en}</span></p>
            <p><span className="font-medium text-card-foreground">Arabic Name:</span> <span className="text-muted-foreground">{selectedRecipe?.name_ar}</span></p>
            <p><span className="font-medium text-card-foreground">Servings:</span> <span className="text-muted-foreground">{formatNumberForDisplay(selectedRecipe?.servings)}</span></p>
            <p><span className="font-medium text-card-foreground">Cost per Serving:</span> <span className="text-muted-foreground">{formatNumberForDisplay(formatCurrency(selectedRecipe?.cost_per_serving || 0))}</span></p>
          </div>
        </div>
        <div className={i18n.language === 'ar' ? 'text-right' : 'text-left'}>
          <h3 className="font-medium text-card-foreground">Cost Summary</h3>
          <div className="mt-2 space-y-1 text-sm">
            <p><span className="font-medium text-card-foreground">Cost per Serving:</span> <span className="text-muted-foreground">{formatNumberForDisplay(formatCurrency(selectedRecipe?.cost_per_serving || 0))}</span></p>
            <p><span className="font-medium text-card-foreground">Total Cost:</span> <span className="text-muted-foreground">{formatNumberForDisplay(formatCurrency((selectedRecipe?.cost_per_serving || 0) * selectedRecipe?.servings))}</span></p>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</div>
);
};

export default Dashboard; 